import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { Send, Loader2, MessageSquare, X } from "lucide-react";
import axios from "axios";

// Define the backend API URLs
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";
const WS_URL = import.meta.env.VITE_WS_URL || "ws://localhost:3000";

interface Message {
  id: string;
  content: string;
  sender: "user" | "bot";
  timestamp: Date;
}

interface WSMessage {
  type: string;
  message?: string;
  messagesRemaining?: number;
}

const ChatBot: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasResume, setHasResume] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messagesRemaining, setMessagesRemaining] = useState(0);
  const { toast } = useToast();

  const ws = useRef<WebSocket | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Connect to WebSocket when component mounts
  useEffect(() => {
    checkForResume();

    // Only connect to WebSocket if the chat is open
    if (isOpen) {
      ws.current = new WebSocket(WS_URL);

      ws.current.onopen = () => {
        setIsConnected(true);
        console.log("WebSocket Connected");

        // Add a welcome message only if there are no existing messages
        setMessages((prev) => {
          // Only add welcome message if messages array is empty
          if (prev.length === 0) {
            return [
              {
                id: Date.now().toString(),
                content:
                  "Hello! My Name is Anas Chatouane, I'm a software developer and I'm here to help you with My resume.",
                sender: "bot",
                timestamp: new Date(),
              },
            ];
          }
          return prev;
        });
      };

      ws.current.onmessage = (event) => {
        console.log("Raw message received:", event.data);
        try {
          // Parse the response as JSON
          const response: WSMessage = JSON.parse(event.data);
          setIsLoading(false);

          if (response.type === "response" && response.message) {
            setMessages((prev) => [
              ...prev,
              {
                id: Date.now().toString(),
                content: response.message,
                sender: "bot",
                timestamp: new Date(),
              },
            ]);

            // Update messages remaining if provided
            if (response.messagesRemaining !== undefined) {
              setMessagesRemaining(response.messagesRemaining);
            }
          } else if (response.type === "error" && response.message) {
            toast({
              variant: "destructive",
              title: "Error",
              description: response.message,
            });
          } else if (
            response.type === "quota" &&
            response.messagesRemaining !== undefined
          ) {
            setMessagesRemaining(response.messagesRemaining);
          }
        } catch (error) {
          console.error("Error parsing WebSocket response:", error);
          // Fallback for plain text responses
          const responseText = event.data.toString();
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now().toString(),
              content: responseText,
              sender: "bot",
              timestamp: new Date(),
            },
          ]);
        }
      };

      ws.current.onerror = (error) => {
        console.error("WebSocket error:", error);
        setIsConnected(false);
        toast({
          variant: "destructive",
          title: "Connection Error",
          description:
            "Failed to connect to chat server. Please try again later.",
        });
      };

      ws.current.onclose = () => {
        console.log("WebSocket Disconnected");
        setIsConnected(false);
      };

      // Clean up on unmount or when chat is closed
      return () => {
        if (ws.current) {
          ws.current.close();
        }
      };
    }
  }, [toast, isOpen]);

  // Check if at least one resume is available
  const checkForResume = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/check-resume-available`);
      setHasResume(response.data.hasResume);

      if (!response.data.hasResume) {
        toast({
          variant: "destructive",
          title: "No Resume Found",
          description: "Please upload a resume before chatting.",
        });
      }
    } catch (error) {
      console.error("Error checking for resume:", error);
      setHasResume(false);
    }
  };

  // Check message quota
  const checkMessageQuota = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/check-message-quota`);
      setMessagesRemaining(response.data.messagesRemaining);
    } catch (error) {
      console.error("Error checking message quota:", error);
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send message to server
  const sendMessage = () => {
    if (!inputMessage.trim() || !isConnected || isLoading) return;

    if (!hasResume) {
      toast({
        variant: "destructive",
        title: "No Resume Found",
        description: "Please upload a resume before chatting.",
      });
      return;
    }

    // Add user message to chat
    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputMessage,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    // Send message to server as JSON
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      const messageData = {
        type: "chat",
        message: inputMessage,
      };
      ws.current.send(JSON.stringify(messageData));
    } else {
      setIsLoading(false);
      toast({
        variant: "destructive",
        title: "Connection Error",
        description: "Not connected to chat server. Please refresh the page.",
      });
    }

    // Focus back on input
    inputRef.current?.focus();
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      sendMessage();
    }
  };

  // Toggle chat open/closed
  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {isOpen ? (
        <Card className="w-80 md:w-96 shadow-lg">
          <CardHeader className="bg-primary text-white rounded-t-lg flex flex-row items-center justify-between p-4">
            <div>
              <CardTitle className="text-lg">Resume Assistant</CardTitle>
              <CardDescription className="text-primary-foreground/90">
                Ask questions about me
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full text-white hover:bg-primary-foreground/20"
              onClick={toggleChat}
            >
              <X className="h-5 w-5" />
            </Button>
          </CardHeader>

          <ScrollArea className="h-96">
            <CardContent className="p-4">
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.sender === "user"
                        ? "justify-end"
                        : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-xs md:max-w-md p-3 rounded-lg ${
                        message.sender === "user"
                          ? "bg-primary text-white"
                          : "bg-muted"
                      }`}
                    >
                      {message.content}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="max-w-xs md:max-w-md p-3 rounded-lg bg-muted flex items-center">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Thinking...
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </CardContent>
          </ScrollArea>

          <CardFooter className="p-3 border-t flex flex-col space-y-2">
            {messagesRemaining > 0 && (
              <div className="w-full text-xs text-muted-foreground text-right">
                {messagesRemaining} messages remaining today
              </div>
            )}
            <div className="flex w-full items-center space-x-2">
              <Input
                ref={inputRef}
                type="text"
                placeholder="Type your question..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={
                  !isConnected ||
                  isLoading ||
                  !hasResume ||
                  messagesRemaining <= 0
                }
                className="flex-1"
              />
              <Button
                size="icon"
                disabled={
                  !isConnected ||
                  isLoading ||
                  !hasResume ||
                  !inputMessage.trim() ||
                  messagesRemaining <= 0
                }
                onClick={sendMessage}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardFooter>
        </Card>
      ) : (
        <Button
          size="icon"
          className="h-12 w-12 rounded-full shadow-lg"
          onClick={toggleChat}
        >
          <MessageSquare className="h-6 w-6" />
        </Button>
      )}
    </div>
  );
};

export default ChatBot;
